// ScoringTestPanel.tsx - Interactive testing panel for scoring configurations

import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { ScoringConfiguration, BaseScoreConfig } from '../../types/ScoringConfiguration';
import { StandardCard, StandardButton } from '../ui';

interface ScoringTestPanelProps {
  configuration: ScoringConfiguration;
  onClose: () => void;
}

interface TestScenario {
  name: string;
  baseModifiers: BaseScoreConfig;
  investmentBonus: BaseScoreConfig;
  incidentImpact: BaseScoreConfig;
  responseTimeBonus: number;
}

export const ScoringTestPanel: React.FC<ScoringTestPanelProps> = ({
  configuration,
  onClose
}) => {
  const { isDark } = useTheme();
  
  const [selectedScenario, setSelectedScenario] = useState<string>('perfect');
  const [customScores, setCustomScores] = useState<BaseScoreConfig>({
    security: 50,
    reputation: 50,
    financial: 50,
    operational: 50
  });

  // Predefined test scenarios
  const testScenarios: Record<string, TestScenario> = {
    perfect: {
      name: 'Perfect Run',
      baseModifiers: { security: 0, reputation: 0, financial: 0, operational: 0 },
      investmentBonus: { security: 30, reputation: 30, financial: 30, operational: 30 },
      incidentImpact: { security: 0, reputation: 0, financial: 0, operational: 0 },
      responseTimeBonus: 10
    },
    average: {
      name: 'Average Performance',
      baseModifiers: { security: 0, reputation: 0, financial: 0, operational: 0 },
      investmentBonus: { security: 15, reputation: 15, financial: 15, operational: 15 },
      incidentImpact: { security: -10, reputation: -10, financial: -10, operational: -10 },
      responseTimeBonus: 5
    },
    poor: {
      name: 'Poor Performance',
      baseModifiers: { security: 0, reputation: 0, financial: 0, operational: 0 },
      investmentBonus: { security: 5, reputation: 5, financial: 5, operational: 5 },
      incidentImpact: { security: -25, reputation: -25, financial: -25, operational: -25 },
      responseTimeBonus: 0
    },
    disaster: {
      name: 'Disaster Scenario',
      baseModifiers: { security: 0, reputation: 0, financial: 0, operational: 0 },
      investmentBonus: { security: 0, reputation: 0, financial: 0, operational: 0 },
      incidentImpact: { security: -40, reputation: -40, financial: -40, operational: -40 },
      responseTimeBonus: 0
    },
    custom: {
      name: 'Custom Scenario',
      baseModifiers: { security: 0, reputation: 0, financial: 0, operational: 0 },
      investmentBonus: { security: 0, reputation: 0, financial: 0, operational: 0 },
      incidentImpact: { security: 0, reputation: 0, financial: 0, operational: 0 },
      responseTimeBonus: 0
    }
  };

  const calculateTestScore = (scenario: TestScenario): BaseScoreConfig => {
    // Simplified scoring: Base (50) + Investments - Incidents
    const finalScores: BaseScoreConfig = {
      security: 50 + scenario.investmentBonus.security + scenario.incidentImpact.security,
      reputation: 50 + scenario.investmentBonus.reputation + scenario.incidentImpact.reputation,
      financial: 50 + scenario.investmentBonus.financial + scenario.incidentImpact.financial,
      operational: 50 + scenario.investmentBonus.operational + scenario.incidentImpact.operational
    };

    // Ensure scores stay within 0-100 range
    Object.keys(finalScores).forEach(key => {
      const category = key as keyof BaseScoreConfig;
      finalScores[category] = Math.max(0, Math.min(100, finalScores[category]));
    });

    return finalScores;
  };

  const calculateOverallScore = (scores: BaseScoreConfig): number => {
    const weights = configuration.categoryWeights;
    return (
      scores.security * weights.security +
      scores.reputation * weights.reputation +
      scores.financial * weights.financial +
      scores.operational * weights.operational
    );
  };

  const getGrade = (score: number): string => {
    const threshold = configuration.gradeThresholds.find(t => score >= t.minScore);
    return threshold?.grade || 'F';
  };

  const currentScenario = testScenarios[selectedScenario];
  const calculatedScores = selectedScenario === 'custom' ? customScores : calculateTestScore(currentScenario);
  const overallScore = calculateOverallScore(calculatedScores);
  const grade = getGrade(overallScore);

  const getCategoryColor = (value: number): string => {
    if (value >= 80) return isDark ? 'text-green-400' : 'text-green-600';
    if (value >= 60) return isDark ? 'text-blue-400' : 'text-blue-600';
    if (value >= 40) return isDark ? 'text-yellow-400' : 'text-yellow-600';
    return isDark ? 'text-red-400' : 'text-red-600';
  };

  const getGradeColor = (grade: string): string => {
    if (grade === 'EXCELLENT') return isDark ? 'text-emerald-400' : 'text-emerald-600';
    if (grade === 'WISE') return isDark ? 'text-green-400' : 'text-green-600';
    if (grade === 'DECENT') return isDark ? 'text-yellow-400' : 'text-yellow-600';
    if (grade === 'OK') return isDark ? 'text-orange-400' : 'text-orange-600';
    if (grade === 'POOR') return isDark ? 'text-red-400' : 'text-red-600';
    // Fallback for letter grades
    if (grade.startsWith('A')) return isDark ? 'text-green-400' : 'text-green-600';
    if (grade.startsWith('B')) return isDark ? 'text-blue-400' : 'text-blue-600';
    if (grade.startsWith('C')) return isDark ? 'text-yellow-400' : 'text-yellow-600';
    if (grade.startsWith('D')) return isDark ? 'text-orange-400' : 'text-orange-600';
    return isDark ? 'text-red-400' : 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <StandardCard variant="outlined" padding="large">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Test Configuration: {configuration.name}
            </h3>
            <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Simulate different scenarios to see how scores are calculated
            </p>
          </div>
          <StandardButton
            onClick={onClose}
            variant="secondary"
            size="small"
          >
            Close
          </StandardButton>
        </div>

        {/* Simplified Scoring Formula */}
        <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'} mb-4`}>
          <p className={`text-center font-mono text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Score = Base (50) + Investments - Incidents
          </p>
          <p className={`text-center text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Incident penalties are reduced by mitigation (max {Math.round(configuration.maxIncidentMitigation * 100)}%)
          </p>
        </div>

        {/* Configuration Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Category Weights
            </p>
            <div className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <div>Security: {Math.round(configuration.categoryWeights.security * 100)}%</div>
              <div>Reputation: {Math.round(configuration.categoryWeights.reputation * 100)}%</div>
              <div>Financial: {Math.round(configuration.categoryWeights.financial * 100)}%</div>
              <div>Operational: {Math.round(configuration.categoryWeights.operational * 100)}%</div>
            </div>
          </div>
          <div>
            <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Max Mitigation
            </p>
            <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {Math.round(configuration.maxIncidentMitigation * 100)}%
            </p>
          </div>
        </div>
      </StandardCard>

      {/* Scenario Selector */}
      <StandardCard variant="outlined" padding="large">
        <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Select Test Scenario
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {Object.entries(testScenarios).map(([key, scenario]) => (
            <StandardButton
              key={key}
              variant={selectedScenario === key ? 'primary' : 'secondary'}
              size="small"
              onClick={() => setSelectedScenario(key)}
              fullWidth
            >
              {scenario.name}
            </StandardButton>
          ))}
        </div>
      </StandardCard>

      {/* Custom Score Inputs (only shown for custom scenario) */}
      {selectedScenario === 'custom' && (
        <StandardCard variant="outlined" padding="large">
          <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Custom Scores
          </h4>
          <div className="grid grid-cols-2 gap-4">
            {Object.entries(customScores).map(([category, value]) => (
              <div key={category}>
                <label className={`block text-sm font-medium mb-2 capitalize ${
                  isDark ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {category}
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={value}
                  onChange={(e) => setCustomScores(prev => ({
                    ...prev,
                    [category]: parseInt(e.target.value)
                  }))}
                  className="w-full"
                />
                <div className="text-center mt-1">
                  <span className={`text-sm font-medium ${getCategoryColor(value)}`}>
                    {value}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </StandardCard>
      )}

      {/* Results Display */}
      <StandardCard variant="elevated" padding="large">
        <h4 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
          Calculated Results
        </h4>

        {/* Score Breakdown */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {Object.entries(calculatedScores).map(([category, value]) => (
            <div key={category} className="text-center">
              <p className={`text-sm font-medium mb-1 capitalize ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {category}
              </p>
              <p className={`text-2xl font-bold ${getCategoryColor(value)}`}>
                {Math.round(value)}
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Weight: {Math.round(configuration.categoryWeights[category as keyof BaseScoreConfig] * 100)}%
              </p>
            </div>
          ))}
        </div>

        {/* Overall Score and Grade */}
        <div className={`border-t pt-6 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="text-center">
            <p className={`text-sm font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Overall Score
            </p>
            <div className="flex items-center justify-center gap-4">
              <p className={`text-4xl font-bold ${getCategoryColor(overallScore)}`}>
                {Math.round(overallScore)}
              </p>
              <div className={`text-5xl font-bold ${getGradeColor(grade)}`}>
                {grade}
              </div>
            </div>
            <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {configuration.gradeThresholds.find(t => t.grade === grade)?.description || 'No description available'}
            </p>
          </div>
        </div>

        {/* Scenario Details (for non-custom scenarios) */}
        {selectedScenario !== 'custom' && (
          <div className={`mt-6 pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <h5 className={`text-sm font-medium mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Scenario Details
            </h5>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Investment Bonus:</span>
                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                  +{Math.round(Object.values(currentScenario.investmentBonus).reduce((a, b) => a + b, 0) / 4)} avg
                </span>
              </div>
              <div className="flex justify-between">
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Incident Impact:</span>
                <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>
                  {Math.round(Object.values(currentScenario.incidentImpact).reduce((a, b) => a + b, 0) / 4)} avg
                </span>
              </div>
            </div>
          </div>
        )}
      </StandardCard>
    </div>
  );
};